// src/pages/dashboard/branch/analytics/components/ItemSelector.tsx
import React, { memo, useRef, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { FiSearch, FiX, FiPackage, FiCheck } from "react-icons/fi";
import useOutsideClick from "../../../../../hooks/useOutsideClick";
import { MenuItem } from "../../../../../utils/supabaseClient";
import { useLocalizedMenuItem } from "../../../../../hooks/useLocalizedMenuItem";
import { useLocalizedCategory } from "../../../../../hooks/useLocalizedCategory";

interface Category {
    id: string;
    name: string;
}

interface ItemSelectorProps {
    selectedItemIds: string[];
    menuItems: MenuItem[];
    categories: Category[];
    maxItems?: number;
    onItemSelect: (itemId: string) => void;
    onItemRemove: (itemId: string) => void;
    disabled?: boolean;
    branchId: string;
}

const SelectedChip = memo(({
    itemId,
    index,
    branchId,
    onRemove,
    disabled
}: {
    itemId: string;
    index: number;
    branchId: string;
    onRemove: (id: string) => void;
    disabled: boolean;
}) => {
    const { localizedName, menuItem } = useLocalizedMenuItem(branchId, itemId);
    
    if (!menuItem) return null;

    const colors = ["emerald", "blue", "orange", "purple", "rose"];
    const colorClass = colors[index % colors.length];

    return (
        <motion.div
            layout
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className={`
                flex items-center gap-2 ps-1 pe-2 py-1 rounded-full text-sm font-medium
                bg-${colorClass}-100 dark:bg-${colorClass}-900/30 
                text-${colorClass}-700 dark:text-${colorClass}-300 
                border-2 border-${colorClass}-300 dark:border-${colorClass}-700
            `}
            style={{
                backgroundColor: `var(--color-${colorClass}-100, #D1FAE5)`,
                color: `var(--color-${colorClass}-700, #047857)`,
                borderColor: `var(--color-${colorClass}-300, #6EE7B7)`,
            }}
        >
            {menuItem.image_url ? (
                <img
                    src={menuItem.image_url}
                    alt=""
                    className="w-6 h-6 rounded-full object-cover"
                />
            ) : (
                <div className="w-6 h-6 rounded-full bg-white/50 flex items-center justify-center">
                    <FiPackage className="w-3 h-3" />
                </div>
            )}
            <span className="truncate max-w-[120px]">{localizedName || menuItem.name}</span>
            <button
                onClick={() => onRemove(itemId)}
                disabled={disabled}
                className="p-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                type="button"
            >
                <FiX className="w-4 h-4" />
            </button>
        </motion.div>
    );
});
SelectedChip.displayName = "SelectedChip";

const ItemOption = memo(({
    item,
    branchId,
    isSelected,
    categoryName,
    onSelect
}: {
    item: MenuItem;
    branchId: string;
    isSelected: boolean;
    categoryName: string;
    onSelect: (id: string) => void;
}) => {
    const { localizedName } = useLocalizedMenuItem(branchId, item.id);
    const { localizedName: localizedCategoryName } = useLocalizedCategory(branchId, item.category_id);

    return (
        <button
            onClick={() => onSelect(item.id)}
            disabled={isSelected}
            className={`
                w-full flex items-center gap-3 px-4 py-3 
                transition-colors text-left
                ${isSelected
                    ? "bg-primary/5 cursor-default"
                    : "hover:bg-gray-50 dark:hover:bg-gray-700"
                }
            `}
            type="button"
        >
            <div className="relative">
                {item.image_url ? (
                    <img
                        src={item.image_url}
                        alt={localizedName || item.name}
                        className="w-10 h-10 rounded-lg object-cover"
                    />
                ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <FiPackage className="w-5 h-5 text-gray-400" />
                    </div>
                )}
                {isSelected && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <FiCheck className="w-3 h-3 text-white" />
                    </div>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <p className={`font-medium truncate ${isSelected
                        ? "text-primary"
                        : "text-gray-900 dark:text-white"
                    }`}>
                    {localizedName || item.name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                    {localizedCategoryName || categoryName}
                </p>
            </div>
        </button>
    );
});
ItemOption.displayName = "ItemOption";

const ItemSelector: React.FC<ItemSelectorProps> = memo(({
    selectedItemIds,
    menuItems,
    categories,
    maxItems = 5,
    onItemSelect,
    onItemRemove,
    disabled = false,
    branchId,
}) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useOutsideClick(dropdownRef, () => setIsDropdownOpen(false));

    const categoriesMap = useMemo(() => {
        const map = new Map<string, string>();
        categories.forEach((cat) => map.set(cat.id, cat.name));
        return map;
    }, [categories]);

    // Note: Search still filters by *English/Original* name or category for now, 
    // unless we pre-calc localized names for all items which is expensive.
    // Ideally we'd search against localized names too, but that requires calling hook for all items.
    // For now we keep search on `item.name` but display localized.
    const filteredItems = useMemo(() => {
        if (!searchQuery.trim()) return menuItems;

        const query = searchQuery.toLowerCase();
        return menuItems.filter((item) => {
            const itemName = item.name.toLowerCase();
            const categoryName = categoriesMap.get(item.category_id || "")?.toLowerCase() || "";
            return itemName.includes(query) || categoryName.includes(query);
        });
    }, [searchQuery, menuItems, categoriesMap]);

    const canAddMore = selectedItemIds.length < maxItems;

    return (
        <div className="space-y-4">
            {/* Label */}
            <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("analytics.selectItemsToCompare")}
                </label>
                <span className={`text-xs font-medium ${selectedItemIds.length >= maxItems
                        ? "text-amber-500"
                        : "text-gray-400"
                    }`}>
                    {selectedItemIds.length}/{maxItems} {t("analytics.selected")}
                </span>
            </div>

            {/* Selected Items */}
            <AnimatePresence>
                {selectedItemIds.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex flex-wrap gap-2"
                    >
                        {selectedItemIds.map((itemId, index) => (
                            <SelectedChip
                                key={itemId}
                                itemId={itemId}
                                index={index}
                                branchId={branchId}
                                onRemove={onItemRemove}
                                disabled={disabled}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Search Input */}
            {canAddMore && (
                <div className="relative" ref={dropdownRef}>
                    <div className="relative">
                        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setIsDropdownOpen(true);
                            }}
                            onFocus={() => setIsDropdownOpen(true)}
                            placeholder={t("analytics.searchItemsPlaceholder")}
                            disabled={disabled}
                            className="w-full pl-11 pr-4 py-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary/50 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                    </div>

                    {/* Dropdown */}
                    <AnimatePresence>
                        {isDropdownOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                transition={{ duration: 0.15 }}
                                className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-h-72 overflow-y-auto"
                            >
                                {filteredItems.length === 0 ? (
                                    <div className="px-4 py-8 text-center text-gray-500">
                                        {t("analytics.noItemsFound")}
                                    </div>
                                ) : (
                                    filteredItems
                                        .slice(0, 15)
                                        .map((item) => {
                                            const isSelected = selectedItemIds.includes(item.id);
                                            const categoryName = categoriesMap.get(item.category_id || "") || "Unknown";

                                            return (
                                                <ItemOption
                                                    key={item.id}
                                                    item={item}
                                                    branchId={branchId}
                                                    isSelected={isSelected}
                                                    categoryName={categoryName}
                                                    onSelect={(id) => {
                                                        onItemSelect(id);
                                                        setSearchQuery("");
                                                        setIsDropdownOpen(false);
                                                    }}
                                                />
                                            );
                                        })
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}

            {/* Max items warning */}
            {!canAddMore && (
                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                    {t("analytics.maxItemsSelected", { maxItems })}
                </p>
            )}
        </div>
    );
});

ItemSelector.displayName = "ItemSelector";

export default ItemSelector;
